// The one dialog for starting a PO by hand (Mitch, 2026-11-04: "I don't want a thousand ways to add a PO. I want stuff
// like this to be deduplicated and still make sense for what we have"). There are three ways in: the Purchasing plan
// (what to buy, with its quantities), a run (its make orders), and this dialog, which every other button opens: New PO
// on Purchase orders and on a vendor's panel (a vendor and a kind), an item panel's Order and Add to PO… on Sell items
// and the Parts library (those items, by vendor). Lines always go on the vendor's open draft of that kind shipping to
// that place (putOnDraft(), openDraftFor()), an item already on it raised, else a new draft, and the dialog says which
// before anything happens. More lines are added on the PO itself, in its one bar (search, the vendor's items, paste).
import { FilePlus2 } from "lucide-react";
import { type KeyboardEvent, useState } from "react";
import type { Item, Vendor, VendorItem } from "../../../../shared/db.gen.ts";
import { PO_KIND_LABELS, validQty } from "../../../../shared/po.ts";
import { qty as fmtQty, plural } from "../../lib/format.ts";
import { usePlan } from "../../lib/planning.ts";
import { useRows } from "../../lib/sync.ts";
import { Button, Input, Select } from "../../ui/controls.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { usePaneOpen } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { openDraftFor, parseNumber, putOnDraft, vendorLabel } from "./lib.ts";

type Kind = "purchase" | "service";
const KINDS: { value: Kind; label: string }[] = [
  { value: "purchase", label: "Purchase (parts and products)" },
  { value: "service", label: "Service (tooling, freight, one-off charges)" },
];

/** Where lines for a vendor go, in words: its open draft, or a new one. */
const targetWords = (draft: { number: string } | null) => (draft ? `Goes on ${draft.number}, its open draft` : "Starts a new draft");

/**
 * New PO: with `items`, those items by their preferred vendor with quantities (the plan's, else the vendor's minimum);
 * otherwise a vendor (`vendorId` to start with) and a kind. Either way onto the vendor's open draft, else a new one,
 * and the PO opens beside the page.
 */
export function NewPoDialog({ vendorId, items, onClose, onDone }: { vendorId?: string; items?: Item[]; onClose: () => void; onDone?: () => void }) {
  const esc = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    }
  };
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex max-h-[80vh] w-[560px] max-w-[calc(100vw-32px)] flex-col rounded-xl border border-line bg-surface p-4 shadow-pop" onKeyDown={esc}>
        {items?.length ? <ItemLines items={items} onClose={onClose} onDone={onDone} /> : <VendorPick vendorId={vendorId} onClose={onClose} />}
      </div>
    </DialogLayer>
  );
}

/** A vendor and a kind: its open draft opens, or a new empty one is made, to add lines to there. */
function VendorPick({ vendorId, onClose }: { vendorId?: string; onClose: () => void }) {
  const vendors = useRows("vendor");
  useRows("po"); // the draft named follows other tabs' changes
  const paneOpen = usePaneOpen();
  const active = vendors.filter((v) => !v.archived).sort((a, b) => vendorLabel(a).localeCompare(vendorLabel(b)));
  const [picked, setPicked] = useState(vendorId ?? "");
  const [kind, setKind] = useState<Kind>("purchase");
  const [busy, setBusy] = useState(false);
  const vendor = active.find((v) => v.id === picked);
  const draft = vendor ? openDraftFor(vendor, undefined, kind) : null;
  const go = async () => {
    if (!vendor) return toast.error("Choose a vendor");
    setBusy(true);
    const po = await putOnDraft(vendor, [], { kind });
    setBusy(false);
    if (!po) return;
    paneOpen("po", po.id);
    onClose();
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void go();
      }}
    >
      <div className="mb-3 text-[15px] font-semibold">New PO</div>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Vendor</span>
          <Select
            autoFocus={!vendorId}
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            options={[{ value: "", label: "Choose a vendor…" }, ...active.map((v) => ({ value: v.id, label: vendorLabel(v) }))]}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Kind</span>
          <Select value={kind} onChange={(e) => setKind(e.target.value as Kind)} options={KINDS} />
        </label>
        <div className="text-[12px] text-muted">
          {vendor
            ? `${targetWords(draft)}. Add its lines there: search, ${vendorLabel(vendor)}'s items or paste.`
            : "Lines go on the vendor's open draft when it has one, so there's never a second."}{" "}
          Make orders start on a run.
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" autoFocus={!!vendorId} disabled={!vendor || busy}>
          {draft ? `Open ${draft.number}` : `Create ${PO_KIND_LABELS[kind].toLowerCase()} draft`}
        </Button>
      </div>
    </form>
  );
}

type Row = { item: Item; link: VendorItem; suggested: number; shipTo: string | undefined };

/** Items picked: by their preferred vendor (and where the plan ships them), each group onto its vendor's open draft. */
function ItemLines({ items, onClose, onDone }: { items: Item[]; onClose: () => void; onDone?: () => void }) {
  const links = useRows("vendor_item");
  const vendors = useRows("vendor");
  useRows("po");
  const plan = usePlan();
  const paneOpen = usePaneOpen();
  const [qtys, setQtys] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const groups = new Map<string, { vendor: Vendor; shipTo: string | undefined; rows: Row[] }>();
  const unlinked: Item[] = [];
  for (const item of items) {
    if (plan.byItem.get(item.id)?.role === "make") continue; // a build: on a run (below)
    const mine = links.filter((l) => l.item_id === item.id);
    const link = mine.find((l) => l.is_preferred) ?? mine[0];
    const vendor = link && vendors.find((v) => v.id === link.vendor_id && !v.archived);
    if (!link || !vendor) {
      unlinked.push(item);
      continue;
    }
    const r = plan.byItem.get(item.id);
    const ours = r?.vendor?.id === vendor.id;
    // Where the plan ships it (a part a CM builds with goes to that CM); else where the vendor's POs go.
    const shipTo = (ours && r?.shipTo) || undefined;
    const suggested = ours && r && r.qty > 0 ? r.qty : validQty(link);
    const key = `${vendor.id}|${shipTo ?? ""}`;
    const g = groups.get(key) ?? { vendor, shipTo, rows: [] };
    g.rows.push({ item, link, suggested, shipTo });
    groups.set(key, g);
  }
  const builds = items.filter((i) => plan.byItem.get(i.id)?.role === "make");
  const list = [...groups.values()];
  const drafts = list.map((g) => openDraftFor(g.vendor, g.shipTo, "purchase"));

  const add = async () => {
    const done: { id: string; number: string; created: boolean }[] = [];
    setBusy(true);
    try {
      for (const g of list) {
        const lines = g.rows.map((r) => ({ item: r.item, link: r.link, qty: parseNumber(qtys[r.item.id] ?? String(r.suggested)) }));
        const bad = lines.find((l) => l.qty == null || l.qty <= 0);
        if (bad) return toast.error(`Enter a quantity for ${bad.item.sku || bad.item.name}`);
        const po = await putOnDraft(
          g.vendor,
          lines.map((l) => ({ ...l, qty: l.qty as number })),
          { shipTo: g.shipTo },
        );
        if (po) done.push(po);
      }
    } finally {
      setBusy(false);
    }
    if (!done.length) return;
    toast.ok(done.map((d) => `${d.created ? "Started" : "Added to"} ${d.number}`).join(" · "));
    if (done.length === 1) paneOpen("po", done[0].id);
    onDone?.();
    onClose();
  };

  const only = list.length === 1 ? drafts[0] : undefined;
  return (
    <>
      <div className="mb-3 text-[15px] font-semibold">Order {items.length === 1 ? items[0].sku || items[0].name : plural(items.length, "item")}</div>
      <div className="flex min-h-0 flex-col gap-3 overflow-y-auto">
        {list.map((g, i) => (
          <div key={`${g.vendor.id}|${g.shipTo ?? ""}`} className="rounded-md border border-line">
            <div className="flex items-baseline gap-2 border-b border-line bg-surface-2/50 px-2 py-1.5">
              <span className="min-w-0 flex-1 truncate font-medium">{vendorLabel(g.vendor)}</span>
              <span className="shrink-0 text-[12px] text-muted">{targetWords(drafts[i])}</span>
            </div>
            {g.rows.map(({ item, link, suggested }) => (
              <div key={item.id} className="flex items-center gap-2 px-2 py-1">
                <span className="w-32 shrink-0 truncate font-mono text-[12px]">{item.sku}</span>
                <span className="min-w-0 flex-1 truncate text-[12px] text-muted" title={item.name}>
                  {item.name}
                </span>
                <span className="text-[11px] text-faint" title={link.moq ? `MOQ ${fmtQty(link.moq)}` : undefined}>
                  {plan.byItem.get(item.id)?.qty ? "the plan's" : link.moq ? "MOQ" : ""}
                </span>
                <Input
                  aria-label={`How many ${item.sku}`}
                  value={qtys[item.id] ?? String(suggested)}
                  onChange={(e) => setQtys({ ...qtys, [item.id]: e.target.value })}
                  className="h-7 w-20 text-right font-mono text-[12px]"
                />
              </div>
            ))}
          </div>
        ))}
        {unlinked.length > 0 && (
          <div className="text-[12px] text-warn">
            No vendor linked, so not added: {unlinked.map((i) => i.sku || i.name).join(", ")}. Link a vendor in the item's panel first.
          </div>
        )}
        {builds.length > 0 && (
          <div className="text-[12px] text-muted">
            {builds.map((i) => i.sku || i.name).join(", ")} {builds.length === 1 ? "is a build" : "are builds"}: builds go on a run, from the Make plan.
          </div>
        )}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" icon={<FilePlus2 className="size-3.5" />} disabled={!list.length || busy} onClick={() => void add()}>
          {only ? `Add to ${only.number}` : list.length === 1 ? "Create draft" : "Add"}
        </Button>
      </div>
    </>
  );
}
